import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Fuel, TrendingDown, TrendingUp } from "lucide-react";

function generateGasData() {
  const data: { hour: string; gwei: number }[] = [];
  let base = 25;
  for (let i = 23; i >= 0; i--) {
    base += (Math.random() - 0.48) * 8;
    base = Math.max(8, Math.min(120, base));
    const h = new Date();
    h.setHours(h.getHours() - i);
    data.push({ hour: `${h.getHours().toString().padStart(2, "0")}:00`, gwei: Math.round(base * 10) / 10 });
  }
  return data;
}

export default function GasHistory() {
  const data = useMemo(generateGasData, []);
  const max = Math.max(...data.map(d => d.gwei));
  const min = Math.min(...data.map(d => d.gwei));
  const current = data[data.length - 1].gwei;
  const avg = Math.round(data.reduce((s, d) => s + d.gwei, 0) / data.length * 10) / 10;
  const trend = current > avg;

  return (
    <Card className="glass border-border/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Fuel className="w-4 h-4 text-primary" /> Gas Price History (24h)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: "Now", value: `${current} gwei`, color: "text-primary" },
            { label: "Avg", value: `${avg} gwei`, color: "text-muted-foreground" },
            { label: "Low", value: `${min} gwei`, color: "text-emerald-500" },
            { label: "High", value: `${max} gwei`, color: "text-red-500" },
          ].map(s => (
            <div key={s.label} className="text-center">
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
              <p className={`text-xs font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground justify-center">
          {trend ? <TrendingUp className="w-3 h-3 text-red-500" /> : <TrendingDown className="w-3 h-3 text-emerald-500" />}
          Gas is {trend ? "above" : "below"} average
        </div>
        <svg viewBox="0 0 480 100" className="w-full h-20">
          <defs>
            <linearGradient id="gasGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={`M0,${100 - ((data[0].gwei - min) / (max - min)) * 80} ${data.map((d, i) => `L${(i / (data.length - 1)) * 480},${100 - ((d.gwei - min) / (max - min)) * 80}`).join(" ")} L480,100 L0,100 Z`}
            fill="url(#gasGrad)"
          />
          <path
            d={data.map((d, i) => `${i === 0 ? "M" : "L"}${(i / (data.length - 1)) * 480},${100 - ((d.gwei - min) / (max - min)) * 80}`).join(" ")}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
          />
        </svg>
        <div className="flex justify-between text-[9px] text-muted-foreground/50">
          <span>{data[0].hour}</span>
          <span>{data[Math.floor(data.length / 2)].hour}</span>
          <span>{data[data.length - 1].hour}</span>
        </div>
      </CardContent>
    </Card>
  );
}
